import React, { useEffect, useState, useMemo } from 'react';
import { Trophy, Music, Loader2, Calendar } from 'lucide-react';
import { toTitleCase } from '../utils/displayNormalization';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Top Artists leaderboard — safe, raw-data display.
 *
 * No estimation, no extrapolation. Just "what we actually saw this week".
 * Fixed default period = previous full week (Mon–Sun) to match the rest of
 * the Broadcast Insights suite.
 *
 * Visual: 3 podium cards for positions 1-3 + ranked list for 4-N.
 */
const TopArtistsLeaderboard = ({ stationId, token, limit = 15 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    const now = new Date();
    const dayIdx = (now.getDay() + 6) % 7; // Mon=0..Sun=6
    const lastSun = new Date(now);
    lastSun.setHours(0, 0, 0, 0);
    lastSun.setDate(now.getDate() - dayIdx - 1);
    const lastMon = new Date(lastSun);
    lastMon.setDate(lastSun.getDate() - 6);
    const fmt = (d) => d.toISOString().split('T')[0];
    return {
      from: fmt(lastMon),
      to: fmt(lastSun),
      labelGr: `Δευ ${String(lastMon.getDate()).padStart(2,'0')}/${String(lastMon.getMonth()+1).padStart(2,'0')} – Κυρ ${String(lastSun.getDate()).padStart(2,'0')}/${String(lastSun.getMonth()+1).padStart(2,'0')}`,
    };
  }, []);

  useEffect(() => {
    if (!stationId || !token) return;
    setLoading(true);
    const q = new URLSearchParams({ from: range.from, to: range.to, limit });
    fetch(`${API}/api/broadcast/top-artists/${stationId}?${q}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [stationId, token, range.from, range.to, limit]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white/60 text-sm p-6 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" />
        Φόρτωση top artists…
      </div>
    );
  }

  if (!data || !data.artists?.length) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center text-sm text-white/60">
        <Music className="w-8 h-8 text-white/30 mx-auto mb-2" />
        Κανένας artist με αρκετά plays αυτή την εβδομάδα.
      </div>
    );
  }

  const { artists, top_plays } = data;
  const podium = artists.slice(0, 3);
  const rest = artists.slice(3);

  const medalColor = (i) =>
    i === 0 ? 'from-amber-400 to-yellow-600 text-yellow-950'
    : i === 1 ? 'from-gray-300 to-gray-500 text-gray-900'
    : 'from-orange-400 to-orange-700 text-orange-950';

  return (
    <div className="space-y-4" data-testid="top-artists-leaderboard">
      {/* Period pill */}
      <div className="inline-flex items-center gap-1.5 text-[11px] text-white/70 bg-white/5 border border-white/10 rounded-md px-2.5 py-1">
        <Calendar className="w-3.5 h-3.5 text-white/50" />
        <span className="text-white/50">Περίοδος:</span>
        <span className="font-medium text-white/85">Προηγούμενη πλήρη εβδομάδα</span>
        <span className="text-white/40">· {range.labelGr}</span>
      </div>

      {/* Podium */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {podium.map((a, i) => (
          <div
            key={a.artist}
            className="relative bg-white/5 border border-white/10 rounded-xl p-4 overflow-hidden"
            data-testid={`top-artist-podium-${i + 1}`}
          >
            <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br opacity-30 blur-xl ${medalColor(i)}`} />
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <div className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br font-bold text-xs ${medalColor(i)}`}>
                  <Trophy className="w-3.5 h-3.5" />
                </div>
                <div className="text-[10px] uppercase text-white/40 font-bold tracking-wider">
                  #{i + 1}
                </div>
              </div>
              <div className="text-white font-semibold text-base leading-tight mb-1 line-clamp-2">
                {toTitleCase(a.artist)}
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-2xl font-bold tabular-nums text-white">{a.plays}</span>
                <span className="text-[11px] text-white/60">plays</span>
              </div>
              <div className="text-[11px] text-white/50 mt-0.5">
                {a.unique_songs} διαφορετικά τραγούδια
              </div>
              {/* Relative bar */}
              <div className="mt-2.5 h-1 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${medalColor(i).split(' ').slice(0,2).join(' ')}`}
                  style={{ width: `${Math.max(5, (a.plays / top_plays) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Rest of the list */}
      {rest.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="divide-y divide-white/5">
            {rest.map((a, i) => (
              <div
                key={a.artist}
                className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.03] transition-colors"
                data-testid={`top-artist-row-${i + 4}`}
              >
                <div className="w-6 text-right text-[11px] font-mono text-white/40">
                  {i + 4}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{toTitleCase(a.artist)}</div>
                  <div className="text-[10px] text-white/50">
                    {a.unique_songs} διαφορετικά τραγούδια
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums text-white">
                    {a.plays}
                  </div>
                  <div className="text-[10px] text-white/50">plays</div>
                </div>
                {/* Thin progress bar */}
                <div className="w-24 hidden md:block h-1 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white/40"
                    style={{ width: `${Math.max(3, (a.plays / top_plays) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopArtistsLeaderboard;
